import { useState } from "react";
import { Loader2, Lock, MessageSquare } from "lucide-react";
import { GuideBlock } from "./GuideBlock";
import { askedFields, fieldKey } from "./connectionsModel";

export const KEY_NAME = /^[A-Z][A-Z0-9_]{2,63}$/;
export const SECRET_VALUE = /^[!-&(-~]{8,4096}$/;
export const PLAIN_VALUE = /^[!-&(-~]{3,4096}$/;

export function validateField(field, value) {
  if (!value) return field.required === false ? "" : `${field.label} is required.`;
  return (field.secret ? SECRET_VALUE : PLAIN_VALUE).test(value) ? "" : `${field.label} must be a single line without spaces or quotes.`;
}

/** One masked or plain input per catalog field. `testIdFor` names each input for the tests. */
export function KeyFields({ fields, values, errors, onChange, testIdFor = (key) => `agent-connection-field-${key}` }) {
  return fields.map((field) => {
    const key = fieldKey(field);
    return (
      <label key={key} className="cx-field">
        <span>{field.label}{field.required === false ? " (optional)" : ""}</span>
        <input
          type={field.secret ? "password" : "text"}
          data-testid={testIdFor(key)}
          placeholder={field.placeholder || ""}
          value={values[key] || ""}
          onChange={(event) => onChange(key, event.target.value.trim())}
          autoComplete="off"
          spellCheck={false}
        />
        {errors[key] ? <em>{errors[key]}</em> : null}
      </label>
    );
  });
}

/** Collects the values of `fields`, checks each, and hands back `{ values, errors }`. */
export function collectFields(fields, values) {
  const errors = {};
  for (const field of fields) {
    const key = fieldKey(field);
    const message = validateField(field, values[key] || "");
    if (message) errors[key] = message;
  }
  const clean = Object.fromEntries(fields.map((field) => fieldKey(field)).filter((key) => values[key]).map((key) => [key, values[key]]));
  return { values: clean, errors };
}

/**
 * The plain key path: the guide above, the fields, Save and connect. Submits `(app, fields)`. `onAsk`, when the page
 * offers it, hands the app to the chat bubble instead: the agent says where the key is, and the key goes in the bubble's field.
 */
export function KeyForm({ app, onSubmit, onCancel, busy = false, submitLabel = "Save and connect", onAsk = null }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const fields = askedFields(app);
  const submit = (event) => {
    event.preventDefault();
    const result = collectFields(fields, values);
    setErrors(result.errors);
    if (Object.keys(result.errors).length) return;
    onSubmit(app.app, result.values);
  };
  return (
    <form className="cx-form" onSubmit={submit} autoComplete="off" data-testid="connection-key-form">
      <GuideBlock guide={app.guide} />
      <KeyFields fields={fields} values={values} errors={errors} onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))} />
      <div className="cx-form__actions">
        <button type="submit" className="cx-btn cx-btn--primary" data-testid="agent-connection-form-submit" disabled={busy}>
          {busy ? <Loader2 size={14} className="cx-spin" /> : <Lock size={14} />}{submitLabel}
        </button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
        {onAsk ? <button type="button" className="cx-btn cx-btn--ghost" data-testid="agent-connection-ask-agent" onClick={onAsk}><MessageSquare size={14} />Ask my agent</button> : null}
      </div>
      <p className="cx-form__note">{app.guide?.reminder ? "" : "Stored encrypted, installed on your agent within about a minute, never shown again."}</p>
    </form>
  );
}

/** The app id a name typed into Something else stands for: lower case, words joined by "_" (what its key's name reads
 *  back as, ELEVENLABS_API_KEY for ElevenLabs), or "" for a name no app id can be made of. */
export function appIdOf(name) {
  const id = String(name || "").normalize("NFKD").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+/, "").slice(0, 40).replace(/_+$/, "");
  return /^[a-z0-9][a-z0-9_-]{1,39}$/.test(id) ? id : "";
}

/** "Something else", with the agent: the app's name, and the agent works out the best way to connect it in the chat
 *  beside the page. Submits `(name, appId)`. */
export function CustomAppForm({ agentName = "your agent", initialName = "", onAsk, onCancel }) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");
  const submit = (event) => {
    event.preventDefault();
    const app = appIdOf(name);
    if (!app) { setError("Type the app's name, like ElevenLabs."); return; }
    setError("");
    onAsk(name.trim(), app);
  };
  return (
    <form className="cx-form" onSubmit={submit} autoComplete="off" data-testid="agent-connection-custom-app-form">
      <label className="cx-field"><span>Which app?</span><input type="text" data-testid="agent-connection-custom-app" value={name} onChange={(event) => setName(event.target.value)} placeholder="ElevenLabs" autoComplete="off" spellCheck={false} /></label>
      {error ? <em className="cx-form__error">{error}</em> : null}
      <div className="cx-form__actions">
        <button type="submit" className="cx-btn cx-btn--primary" data-testid="agent-connection-custom-app-ask"><MessageSquare size={14} />Ask {agentName} to connect it</button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </div>
      <p className="cx-form__note">{agentName[0].toUpperCase() + agentName.slice(1)} finds the best way: a key in a field beside the chat, the app's own sign-in, or its own browser.</p>
    </form>
  );
}

/** "Something else": a key the agent expects under a name only the person knows. */
export function CustomKeyForm({ onSubmit, onCancel, busy = false, pattern = KEY_NAME, initialKey = "" }) {
  const [envKey, setEnvKey] = useState(initialKey);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const keyPattern = typeof pattern === "string" ? new RegExp(pattern) : pattern;
  const submit = (event) => {
    event.preventDefault();
    if (!keyPattern.test(envKey)) { setError("Use capital letters, numbers and underscores, like LOOP_API_TOKEN."); return; }
    if (!SECRET_VALUE.test(value)) { setError("The value must be a single line without spaces or quotes."); return; }
    setError("");
    onSubmit(envKey, value);
  };
  return (
    <form className="cx-form" onSubmit={submit} autoComplete="off">
      <label className="cx-field"><span>Key name your agent expects</span><input type="text" data-testid="agent-connection-custom-key" value={envKey} onChange={(event) => setEnvKey(event.target.value.trim())} placeholder="LOOP_API_TOKEN" autoComplete="off" spellCheck={false} /></label>
      <label className="cx-field"><span>Value</span><input type="password" data-testid="agent-connection-custom-value" value={value} onChange={(event) => setValue(event.target.value.trim())} autoComplete="off" /></label>
      {error ? <em className="cx-form__error">{error}</em> : null}
      <div className="cx-form__actions">
        <button type="submit" className="cx-btn cx-btn--primary" data-testid="agent-connection-form-submit" disabled={busy}>{busy ? <Loader2 size={14} className="cx-spin" /> : <Lock size={14} />}Save and connect</button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </div>
      <p className="cx-form__note">Stored encrypted, installed on your agent within about a minute, never shown again.</p>
    </form>
  );
}

export default KeyForm;
